// Premium upsell, in two shapes from one source of truth.
//
//   "wide"   — a strip. Explore, where the page is a stack of full-width rows.
//   "square" — a panel with a looping Veo animation behind it. Home, where a wide bar under
//              the lesson row reads as a footer and gets skipped.
//
// Both live here rather than in the pages because the CLAIMS must not drift: every one is
// taken from settings.SUBSCRIPTION_PLANS.premium (120 credits at $29.99) and from
// SUBSCRIBER_ONLY_MODES, which genuinely contains mode_hd. A promo that drifts from the plan
// it sells is worse than no promo, and two copies is how that drift starts.
import React from "react";
import { Check } from "lucide-react";
import { autoplayVideo } from "../utils/autoplayVideo";

const PREMIUM_BENEFITS = [
  "120 videos every month",
  "HD lessons — 2560×1440 at 60fps",
];

const Benefit = ({ children, className = "" }) => (
  <li className={`flex items-center gap-2 text-white/85 ${className}`}>
    <Check className="w-4 h-4 text-[#4ADE80] flex-shrink-0" strokeWidth={3} />
    {children}
  </li>
);

const Wordmark = () => (
  <div className="flex items-center gap-2">
    <img src="/magi-icon.svg" alt="" className="w-6 h-6 rounded-md" />
    <span className="text-white/70 text-xs font-bold tracking-[0.18em] uppercase">Magi</span>
  </div>
);

const PremiumBanner = ({ onClick, variant = "wide", className = "" }) => {
  if (variant === "square") {
    return (
      <button
        onClick={onClick}
        aria-label="See Premium plans"
        className={`group relative aspect-[16/10] w-full overflow-hidden rounded-2xl bg-black
          border border-white/10 hover:border-white/25 transition-colors text-left ${className}`}
      >
        {/* Muted, looping, playsInline and autoPlay together are what let this start on its
            own on iOS — drop any one of them and it renders a black box with a play button.
            The file is a ping-pong of a Veo clip, so the restart is seamless rather than a
            visible jump, and it is deliberately silent: an ad that makes noise is a bug.
            AND THEY ARE STILL NOT ENOUGH IN LOW POWER MODE, which blocks autoplay outright
            however the element is marked up. The poster is the clip's own first frame, so
            that case degrades to a still instead of to a black rectangle. */}
        <video
          ref={autoplayVideo}
          src="/promo/premium-square.mp4"
          poster="/promo/premium-square.jpg"
          className="absolute inset-0 h-full w-full object-cover"
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          aria-hidden="true"
          tabIndex={-1}
        />
        {/* Legibility scrim, retuned with the Kurzgesagt clip that replaced the neon loop. Same
            reasoning as HomeShortcuts: the old ramp held 0.72 black a third of the way up and
            veiled the top edge at 0.35, which is right over neon-on-black and turns flat vector
            colour grey. Now it clears completely above 58%. It still reaches higher than the
            shortcut cards' scrim because this panel's copy block is taller — a 4xl headline over
            two benefit lines, not one line of body text. */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(to top, rgba(0,0,0,0.90) 0%, rgba(0,0,0,0.70) 22%, rgba(0,0,0,0.25) 42%, rgba(0,0,0,0) 58%)",
          }}
        />

        <div className="absolute inset-0 flex flex-col justify-between p-6">
          <Wordmark />
          <div>
            <p className="text-4xl font-bold leading-none text-white mb-4">
              Try <span className="text-[#8B7BFF]">Premium</span>
            </p>
            <ul className="space-y-1.5">
              {PREMIUM_BENEFITS.map((b) => (
                <Benefit key={b} className="text-[15px]">{b}</Benefit>
              ))}
            </ul>
          </div>
        </div>
      </button>
    );
  }

  return (
    <button
      onClick={onClick}
      aria-label="See Premium plans"
      className={`group w-full text-left rounded-2xl overflow-hidden bg-black border border-white/10
        hover:border-white/25 transition-colors relative ${className}`}
    >
      <div className="flex items-center gap-6 px-6 sm:px-8 py-6 sm:py-7">
        <div className="flex-shrink-0">
          <div className="mb-1.5">
            <Wordmark />
          </div>
          <p className="text-3xl sm:text-4xl font-bold leading-none text-white">
            Try <span className="text-[#8B7BFF]">Premium</span>
          </p>
        </div>

        {/* The art is rendered on pure black, which is why the bar is too — on any other dark
            the image's rectangular edge shows. Hidden below lg, where headline and benefits
            would collide with it. */}
        <img
          src="/promo/premium-icons.webp"
          alt=""
          aria-hidden="true"
          className="hidden lg:block flex-1 min-w-0 h-[124px] object-contain object-center select-none pointer-events-none"
          loading="lazy"
          decoding="async"
        />

        <ul className="flex-shrink-0 space-y-1.5 ml-auto">
          {PREMIUM_BENEFITS.map((b) => (
            <Benefit key={b} className="text-sm sm:text-[15px]">{b}</Benefit>
          ))}
        </ul>
      </div>
    </button>
  );
};

export default PremiumBanner;
